import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import {
  NEW_VISIT,
  UPDATE_VISIT,
  DELETE_VISIT,
} from "../utils/GraphQL/mutations";

// One newVisit per tab session, even across rapid client-side navigations or a
// double-mount. Module scope (not component state) is the correct lifetime: it
// lives for the tab's JS context, independent of any <Tracking> mount/unmount.
// Concurrent mounts await the SAME in-flight create instead of each firing their
// own — that's the fix for the write-after-await duplicate-visitor race. Cleared
// in finally so a genuinely new session (after removeVisit) can create again.
let createInFlight = null;

const Tracking = ({ page, convertQuote, removeVisit }) => {
  const [newVisit] = useMutation(NEW_VISIT);
  const [updateVisit] = useMutation(UPDATE_VISIT);
  const [deleteVisit] = useMutation(DELETE_VISIT);
  const { scanned } = useParams();

  useEffect(() => {
    if (removeVisit !== true) {
      visitorCheck(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (convertQuote) {
      updateQuoted();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertQuote]);

  useEffect(() => {
    if (removeVisit === true) {
      visitDeleteHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeVisit]);

  const updateQuoted = async () => {
    // a fast submit can fire the conversion while the create is still in flight;
    // wait it out so the id is persisted before we read it (else convert is lost)
    if (createInFlight) {
      try {
        await createInFlight;
      } catch (_) {
        /* ignore — fall through to the id check */
      }
    }
    const visitId = sessionStorage.getItem("bdr-visitor");
    if (!visitId) return; // no visit row yet → nothing to attribute the conversion to

    if (convertQuote === true) {
      try {
        await updateVisit({
          variables: {
            updateVisitId: visitId,
            convertQuote: convertQuote,
          },
        });
      } catch (_) {
        // analytics conversion update must never surface to the visitor
      }
    }
  };

  const visitDeleteHandler = async () => {
    // A create may still be in flight (staff navigated mid-create). Wait it out
    // so the delete can't race ahead of the create and orphan the new row.
    if (createInFlight) {
      try {
        await createInFlight;
      } catch (_) {
        /* ignore — proceed to read whatever id landed */
      }
    }
    const visitId = sessionStorage.getItem("bdr-visitor");

    if (!visitId || removeVisit !== true) {
      return;
    }

    // Best-effort, ONE-SHOT visitor-analytics cleanup. ALWAYS clear the local
    // id — even on failure — so a slow/failed delete can never re-fire on a
    // re-render or reload. (Previously it only cleared on success; combined
    // with the admin-gated delete being coded UNAUTHENTICATED, a failure drove
    // errorLink → /login-portal redirect → reload → re-fire → flood loop.)
    try {
      await deleteVisit({
        variables: {
          deleteVisitId: visitId,
        },
      });
    } catch (_) {
      // Swallow — cleanup of a visit record must never block or loop the page.
    } finally {
      sessionStorage.removeItem("bdr-visitor");
    }
  };

  const visitorCheck = async (pageVisited) => {
    const nonVisitor = localStorage.getItem("bdr-non-visitor");
    if (nonVisitor === "true") {
      return;
    }

    const visitId = sessionStorage.getItem("bdr-visitor");
    const time = Date.now();

    if (!visitId) {
      // Dedupe to ONE newVisit per tab session. Concurrent mounts (rapid
      // navigation, double-mount) await the SAME in-flight create rather than
      // each firing their own → no duplicate visitor rows. On failure the guard
      // clears in finally so a later mount can retry (no poisoned sentinel).
      if (!createInFlight) {
        createInFlight = newVisit({
          variables: {
            timeVisited: time.toString(),
            signNum: scanned,
            pageVisited,
          },
        })
          .then(({ data }) => {
            const id = data?.newVisitor?.id;
            if (id) sessionStorage.setItem("bdr-visitor", id);
            return id;
          })
          .catch(() => null)
          .finally(() => {
            createInFlight = null;
          });
      }
      await createInFlight;
      return;
    }

    try {
      await updateVisit({
        variables: {
          updateVisitId: visitId,
          page: pageVisited || "home",
        },
      });
    } catch (_) {
      // analytics page-update must never surface to the visitor
    }
  };

  return <></>;
};

export default Tracking;
